// Curated multi-LLM playbooks for the home-page chat runner.
//
// Each playbook is a short recipe (3–5 steps) for accomplishing a real
// task by combining different AI models. The point is to teach beginners
// that no single model is best for everything: brainstorming favors a
// fast/cheap creative model, polishing favors a stronger reasoning one,
// research favors a model with web grounding, etc.
//
// Per-step shape:
//   - prompt:       the prompt template the runner will send (with a
//                   `{topic}` placeholder filled from the user's search
//                   query, when present).
//   - model:        which Lovable AI Gateway model to use.
//   - modelLabel:   short human-friendly name shown in the UI badge.
//   - reason:       one-sentence explanation of WHY this model was picked.
//   - compareWith:  optional second model id; the runner shows a
//                   side-by-side "compare" button on these steps so the
//                   learner can see how outputs differ across models.

export type PlaybookStep = {
  title: string;
  prompt: string;
  model: string;
  modelLabel: string;
  reason: string;
  compareWith?: { model: string; modelLabel: string };
};

export type Playbook = {
  slug: string;
  title: string;
  tagline: string;
  emoji: string;
  steps: PlaybookStep[];
};

// Model shortcuts so we don't sprinkle string literals everywhere.
const FLASH = {
  id: "google/gemini-3-flash-preview",
  label: "Gemini 3 Flash",
};
const FLASH_LITE = {
  id: "google/gemini-2.5-flash-lite",
  label: "Gemini Flash Lite",
};
const PRO = {
  id: "google/gemini-2.5-pro",
  label: "Gemini 2.5 Pro",
};
const GPT5_MINI = {
  id: "openai/gpt-5-mini",
  label: "GPT-5 Mini",
};
const GPT5 = {
  id: "openai/gpt-5",
  label: "GPT-5",
};

export const PLAYBOOKS: Playbook[] = [
  {
    slug: "long-doc-to-action",
    title: "Turn a long doc into a 5-action plan",
    tagline: "Summarize cheaply, then reason over the summary.",
    emoji: "📄",
    steps: [
      {
        title: "Extract the key points",
        prompt:
          "Summarize this document into 8 key bullets, preserving any numbers or deadlines: {topic}",
        model: FLASH.id,
        modelLabel: FLASH.label,
        reason: "Big context window + fast — ideal for chewing through long input.",
      },
      {
        title: "Turn bullets into actions",
        prompt:
          "Convert the bullets above into exactly 5 concrete next actions, each with an owner and a deadline. Format as a numbered list.",
        model: GPT5_MINI.id,
        modelLabel: GPT5_MINI.label,
        reason: "Better structured-output reliability than Flash for plans.",
      },
      {
        title: "Sanity-check the plan",
        prompt:
          "Review the 5 actions. Flag any that are vague, conflict, or are missing a clear deliverable. Suggest one fix per flag.",
        model: PRO.id,
        modelLabel: PRO.label,
        reason: "Pro tier catches contradictions a Flash model often misses.",
      },
    ],
  },
  {
    slug: "cold-email-sequence",
    title: "Write a 3-email cold outreach sequence",
    tagline: "Draft fast, then humanize.",
    emoji: "✉️",
    steps: [
      {
        title: "Draft all 3 emails at once",
        prompt:
          "Write a 3-email cold outreach sequence for: {topic}. Email 1 = intro + hook. Email 2 = soft proof + ask. Email 3 = breakup. Subject lines included.",
        model: FLASH.id,
        modelLabel: FLASH.label,
        reason: "Fast + cheap — drafts a full sequence in one shot.",
      },
      {
        title: "Make it sound human",
        prompt:
          "Rewrite the sequence above to sound like a real person, not marketing copy. Cut clichés, vary sentence length, drop one casual phrase per email.",
        model: GPT5.id,
        modelLabel: GPT5.label,
        reason: "GPT-5 is best at natural, non-AI-sounding voice.",
        compareWith: { model: PRO.id, modelLabel: PRO.label },
      },
    ],
  },
  {
    slug: "explain-like-im-five",
    title: "Explain a complex topic, then deepen it",
    tagline: "Start simple. Add layers as needed.",
    emoji: "🧠",
    steps: [
      {
        title: "ELI5 explanation",
        prompt:
          "Explain this concept to a curious 10-year-old in 4 short paragraphs. Use one analogy. Topic: {topic}",
        model: FLASH_LITE.id,
        modelLabel: FLASH_LITE.label,
        reason: "Cheapest tier handles simple explanations perfectly.",
      },
      {
        title: "Add expert nuance",
        prompt:
          "Now layer in 3 nuances an expert would care about that the simple version glossed over. Keep each under 3 sentences.",
        model: PRO.id,
        modelLabel: PRO.label,
        reason: "Stronger reasoning surfaces the trade-offs Flash skips.",
      },
    ],
  },
  {
    slug: "landing-page-copy",
    title: "Generate landing page copy that converts",
    tagline: "Many drafts, one ruthless edit.",
    emoji: "🪄",
    steps: [
      {
        title: "Generate 5 hero headline + subhead pairs",
        prompt:
          "Write 5 hero headline + subheadline pairs for a landing page about: {topic}. Mix benefit-led, problem-led, and curiosity-led angles.",
        model: FLASH.id,
        modelLabel: FLASH.label,
        reason: "Volume-friendly creative work — no need for a heavy model.",
      },
      {
        title: "Pick the strongest + write supporting sections",
        prompt:
          "Pick the best pair from the previous step. Then write: a 3-bullet 'how it works' section, a 1-paragraph proof block, and a CTA button label.",
        model: GPT5_MINI.id,
        modelLabel: GPT5_MINI.label,
        reason: "Better at structured marketing copy than Flash.",
        compareWith: { model: PRO.id, modelLabel: PRO.label },
      },
    ],
  },
  {
    slug: "decode-error-message",
    title: "Decode a confusing error message",
    tagline: "Quick first take, deep second pass.",
    emoji: "🐛",
    steps: [
      {
        title: "First-pass diagnosis",
        prompt:
          "I'm hitting this error: {topic}. In plain English, what does it mean and what are the 3 most likely causes?",
        model: FLASH.id,
        modelLabel: FLASH.label,
        reason: "Fast triage — Flash handles common errors instantly.",
      },
      {
        title: "Step-by-step fix plan",
        prompt:
          "Now write a numbered debugging plan to fix this error: try the cheapest fix first, then escalate. Include exactly what to check at each step.",
        model: GPT5.id,
        modelLabel: GPT5.label,
        reason: "GPT-5 reasons through technical fixes more reliably than Flash.",
      },
    ],
  },
  {
    slug: "youtube-to-blog",
    title: "Turn a YouTube transcript into a blog post",
    tagline: "Compress, then craft.",
    emoji: "🎬",
    steps: [
      {
        title: "Extract the spine",
        prompt:
          "From this transcript, extract the main thesis, 5 supporting points, and 3 quotable lines. Transcript: {topic}",
        model: FLASH.id,
        modelLabel: FLASH.label,
        reason: "Long context window handles full transcripts cheaply.",
      },
      {
        title: "Write the blog post",
        prompt:
          "Using the spine above, write a 500-word blog post with a strong intro hook, the 5 points as H2 sections, and a memorable closer. Use one of the quotes as a pull-quote.",
        model: GPT5_MINI.id,
        modelLabel: GPT5_MINI.label,
        reason: "Better long-form prose flow than Flash.",
        compareWith: { model: PRO.id, modelLabel: PRO.label },
      },
    ],
  },
  {
    slug: "ship-side-project",
    title: "Plan a 7-day side project sprint",
    tagline: "Ideate broadly, then commit.",
    emoji: "🛠️",
    steps: [
      {
        title: "Generate 5 project ideas",
        prompt:
          "I want to build a small AI side project this week. Theme: {topic}. List 5 buildable ideas with a one-line user value prop each.",
        model: FLASH_LITE.id,
        modelLabel: FLASH_LITE.label,
        reason: "Idea generation is the cheapest possible AI task.",
      },
      {
        title: "Pick one + scope a 7-day plan",
        prompt:
          "Pick the most achievable idea. Build a 7-day plan: one concrete deliverable per day, ending in something I could share publicly on day 7.",
        model: PRO.id,
        modelLabel: PRO.label,
        reason: "Pro plans realistic timelines better than Flash.",
      },
    ],
  },
  {
    slug: "prep-for-meeting",
    title: "Prep for a high-stakes meeting in 10 minutes",
    tagline: "Brief, then anticipate.",
    emoji: "🎯",
    steps: [
      {
        title: "Write a 1-pager brief",
        prompt:
          "I have a meeting about: {topic}. Write a 1-page brief: context, my goal, 3 talking points, and the single decision I want by the end.",
        model: FLASH.id,
        modelLabel: FLASH.label,
        reason: "Fast structured drafting — Flash is plenty for a brief.",
      },
      {
        title: "Predict the hard questions",
        prompt:
          "List the 5 hardest questions I'll get asked in this meeting and a 2-sentence answer for each. Be honest about weaknesses.",
        model: GPT5.id,
        modelLabel: GPT5.label,
        reason: "GPT-5 is best at adversarial what-if reasoning.",
        compareWith: { model: PRO.id, modelLabel: PRO.label },
      },
    ],
  },
  {
    slug: "feedback-into-roadmap",
    title: "Turn raw user feedback into a roadmap",
    tagline: "Cluster cheaply, prioritize sharply.",
    emoji: "🗺️",
    steps: [
      {
        title: "Cluster the feedback",
        prompt:
          "Cluster this user feedback into 5 themes. For each theme: name, count of mentions, one representative quote. Feedback: {topic}",
        model: FLASH.id,
        modelLabel: FLASH.label,
        reason: "Bulk text classification is Flash's sweet spot.",
      },
      {
        title: "Prioritize with RICE",
        prompt:
          "For each theme, score Reach / Impact / Confidence / Effort (1-5 each) and compute RICE = (R × I × C) / E. Sort highest-first. Recommend the top 2 to ship next.",
        model: PRO.id,
        modelLabel: PRO.label,
        reason: "Numerical prioritization needs the strongest reasoning model.",
      },
    ],
  },
  // ──────────────────────────────────────────────────────────────────
  // AI App Builder playbooks — one per builder profiled in
  // src/data/aiAppBuilders.ts. Each is a 2-step coach: first nail
  // the brief, then write a copy-paste prompt the user can drop into
  // that builder's chat box. We keep these short on purpose — the
  // *builder* itself does the heavy lifting; this is the prep work.
  // Slugs and titles include the builder name so PlaybookSearchHero's
  // pickPlaybook keyword match routes the rotator chip → right runner.
  // ──────────────────────────────────────────────────────────────────
  {
    slug: "ship-mvp-with-lovable",
    title: "Ship an MVP with Lovable",
    tagline: "Sharpen the brief, then hand it to the chat-builder.",
    emoji: "💖",
    steps: [
      {
        title: "Pressure-test the idea",
        prompt:
          "I want to build an MVP for: {topic}. Ask me 5 sharp questions to lock down the user, the single core action, and the must-have backend (auth? db? payments?). Be skeptical — flag anything that sounds like scope creep.",
        model: GPT5.id,
        modelLabel: GPT5.label,
        reason: "GPT-5 asks the most useful product-discovery questions.",
      },
      {
        title: "Draft the Lovable prompt",
        prompt:
          "Now write a single, copy-paste-ready prompt I can drop into Lovable's chat box. It should specify: app name, 1-line purpose, the 3 core pages, the data model in plain English, auth requirements, and visual style. Keep it under 250 words.",
        model: GPT5_MINI.id,
        modelLabel: GPT5_MINI.label,
        reason: "Mini is great for tight, structured prompt-writing.",
      },
    ],
  },
  {
    slug: "scaffold-with-replit-agent",
    title: "Scaffold a project with Replit Agent",
    tagline: "Pick the stack, then write the agent brief.",
    emoji: "🛠️",
    steps: [
      {
        title: "Choose the stack",
        prompt:
          "I want to build: {topic} on Replit. Recommend a stack (language + framework + DB) optimized for solo-dev velocity and easy deploy on Replit. Justify each pick in one sentence.",
        model: PRO.id,
        modelLabel: PRO.label,
        reason: "Pro reasons well across language tradeoffs.",
      },
      {
        title: "Write the Replit Agent brief",
        prompt:
          "Now write a single brief I can paste into Replit Agent. Include: project goal, stack chosen above, file structure, the first 3 commands the agent should run, and the success criteria for the first iteration.",
        model: GPT5_MINI.id,
        modelLabel: GPT5_MINI.label,
        reason: "Tight structured output without overspending.",
      },
    ],
  },
  {
    slug: "internal-tool-with-base44",
    title: "Build an internal tool with Base44",
    tagline: "Nail the workflow, then describe it once.",
    emoji: "🧰",
    steps: [
      {
        title: "Map the workflow",
        prompt:
          "I want to build an internal tool for: {topic}. Map the workflow in 5–7 numbered steps from the user's perspective. For each step, name the screen, the inputs, and what gets saved.",
        model: FLASH.id,
        modelLabel: FLASH.label,
        reason: "Fast structured drafting — Flash handles workflow mapping cleanly.",
      },
      {
        title: "Compose the Base44 brief",
        prompt:
          "Convert that workflow into a single brief for Base44. List: app name, user roles, screens with their fields, the data model (tables + columns), and any list/detail views. Be specific enough that a builder could ship a working v1 from this alone.",
        model: GPT5_MINI.id,
        modelLabel: GPT5_MINI.label,
        reason: "Mini is reliable on structured spec output.",
      },
    ],
  },
  {
    slug: "landing-page-with-primio",
    title: "Ship a landing page with Primio",
    tagline: "Position first, then write the build prompt.",
    emoji: "🎨",
    steps: [
      {
        title: "Lock the positioning",
        prompt:
          "I'm shipping a landing page for: {topic}. Draft: a 6-word headline, a 1-sentence subheadline, the 3 strongest value props, the target audience, and the single CTA. Be specific — no marketing fluff.",
        model: GPT5.id,
        modelLabel: GPT5.label,
        reason: "GPT-5's copywriting beats the cheaper models on first-pass landing copy.",
      },
      {
        title: "Draft the Primio brief",
        prompt:
          "Turn the positioning above into a single prompt for Primio. Specify the section order (hero, social proof, features, pricing, FAQ, CTA), tone (e.g. 'minimal hacker zine'), color direction, and the single CTA action. End with 'Ship as a single page, mobile-first.'",
        model: GPT5_MINI.id,
        modelLabel: GPT5_MINI.label,
        reason: "Mini drafts tight, on-spec build prompts cheaply.",
      },
    ],
  },
  {
    slug: "prototype-with-bolt",
    title: "Prototype an app with Bolt.new",
    tagline: "Decide the smallest shippable slice, then prompt it.",
    emoji: "⚡",
    steps: [
      {
        title: "Cut the scope to a v0.1",
        prompt:
          "I want to prototype: {topic} in Bolt.new. Strip the scope down to the smallest version that proves the idea works in a single browser session (no backend if avoidable). List what's IN and what's OUT.",
        model: PRO.id,
        modelLabel: PRO.label,
        reason: "Pro is best at brutal scope-cutting.",
      },
      {
        title: "Write the Bolt prompt",
        prompt:
          "Now write a single prompt for Bolt.new. Specify: framework (Vite + React or Next.js), the one screen to build, the local-state shape, and any libraries to install with npm. Keep it self-contained — no external APIs unless I named one.",
        model: GPT5_MINI.id,
        modelLabel: GPT5_MINI.label,
        reason: "Mini handles tight, dev-flavored prompts well.",
      },
    ],
  },
  {
    slug: "ui-with-v0",
    title: "Generate a UI with v0 by Vercel",
    tagline: "Pin the design language, then describe the components.",
    emoji: "🟣",
    steps: [
      {
        title: "Define the design language",
        prompt:
          "I'm generating a UI in v0 for: {topic}. Pick a clear design direction in 5 lines: vibe (e.g. 'editorial', 'brutalist'), primary color (hex), font pair, density, and the single most important interaction. Justify each pick in one short clause.",
        model: GPT5.id,
        modelLabel: GPT5.label,
        reason: "GPT-5's taste beats cheaper models on UI direction.",
      },
      {
        title: "Compose the v0 prompt",
        prompt:
          "Now write a single prompt for v0. Describe the screen as a list of shadcn components in render order, their props/state, the data shape they consume, and any empty/loading/error states. End with 'Use Tailwind tokens, no custom CSS files.'",
        model: GPT5_MINI.id,
        modelLabel: GPT5_MINI.label,
        reason: "Mini is tight + reliable for component-spec prompts.",
      },
    ],
  },
];